<template>
  <v-dialog :model-value="modelValue" persistent max-width="420" @update:model-value="onUpdate">
    <div class="hint-card">
      <div class="hint-header">
        <span class="hint-badge"><v-icon size="24" color="#64ffda">mdi-gesture-tap</v-icon></span>
        <div>
          <div class="hint-eyebrow">Quick tip</div>
          <h2 class="hint-title">Getting around</h2>
        </div>
      </div>

      <p class="hint-sub">A few touch gestures for exploring the 3D model:</p>

      <ul class="hint-list">
        <li class="hint-row">
          <span class="hint-icon"><v-icon size="22" color="#66c7b4">mdi-rotate-3d-variant</v-icon></span>
          <div class="hint-text">
            <strong>One finger</strong>
            <span>Drag to rotate the model</span>
          </div>
        </li>
        <li class="hint-row">
          <span class="hint-icon"><v-icon size="22" color="#66c7b4">mdi-gesture-spread</v-icon></span>
          <div class="hint-text">
            <strong>Two fingers</strong>
            <span>Pinch to zoom, drag to pan</span>
          </div>
        </li>
        <li class="hint-row">
          <span class="hint-icon"><v-icon size="22" color="#66c7b4">mdi-gesture-tap-hold</v-icon></span>
          <div class="hint-text">
            <strong>Press &amp; hold</strong>
            <span>Open an anomaly or hotspot's details</span>
          </div>
        </li>
      </ul>

      <button type="button" class="hint-btn" @click="acknowledge">Got it</button>
    </div>
  </v-dialog>
</template>

<script setup>
import { defineEmits, defineProps } from 'vue';

defineProps({
  modelValue: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'acknowledge']);

const acknowledge = () => {
  emit('acknowledge');
  emit('update:modelValue', false);
};

// Persistent dialog won't close on scrim/esc, but guard anyway.
const onUpdate = (value) => {
  if (!value) emit('update:modelValue', false);
};
</script>

<style scoped>
.hint-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 18px;
  padding: 22px 22px 20px;
  color: #fff;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45);
}

.hint-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.hint-badge {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(100, 255, 218, 0.1);
  border: 1px solid rgba(100, 255, 218, 0.25);
}

.hint-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.hint-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 2px 0 0;
  color: #fff;
}

.hint-sub {
  font-size: 0.9rem;
  color: #b0bec5;
  margin: 0 0 16px;
  line-height: 1.45;
}

.hint-list {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hint-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.hint-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(102, 199, 180, 0.08);
  border: 1px solid rgba(102, 199, 180, 0.18);
}

.hint-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.hint-text strong {
  font-size: 0.92rem;
  font-weight: 700;
  color: #fff;
}

.hint-text span {
  font-size: 0.84rem;
  color: #8aa0aa;
  line-height: 1.35;
}

.hint-btn {
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  background: var(--duallo-accent-gradient);
  color: #071514;
  border: none;
  transition: filter 0.15s ease;
}

.hint-btn:hover {
  filter: brightness(1.05);
}
</style>
